// Přepisy schůzek: odkazy v odpovědi agenta a tvar přepisu, který appka zobrazí v panelu.
// Agent cituje místo v přepisu jako `[2026-10-12 10:32, 00:54:29]`: začátek schůzky (datum a čas) a čas od začátku nahrávky.

export interface TranscriptSegment {
  /** Sekundy od začátku nahrávky. */
  t: number;
  speaker: string | null;
  text: string;
}

export interface TranscriptDTO {
  id: string;
  title: string;
  client: string | null;
  /** Začátek schůzky v místním čase, `YYYY-MM-DDTHH:MM`. */
  start: string;
  durationSec: number | null;
  segments: TranscriptSegment[];
  /** ID schůzky u Sama (`2026-10-12 10:32`, druhá nahrávka téže schůzky `…b`). */
  meetingId?: string;
  /** Odkaz na nahrávku v SharePointu. */
  sharepoint?: string | null;
}

/** Víc schůzek se stejným začátkem: uživatel vybere, appka pak načte podle meetingId. */
export interface TranscriptCandidate {
  meetingId: string;
  client: string | null;
  title: string;
}

export interface TranscriptRef {
  /** `YYYY-MM-DDTHH:MM` */
  start: string;
  /** Sekundy od začátku nahrávky. */
  t: number;
  /** Konkrétní nahrávka, když jich má stejný začátek víc (seznam v Přepisech). */
  meetingId?: string;
}

/** Stav zpracování nahrávky u Sama (přepis hotový, nebo ještě čeká). */
export type TranscriptStatus = 'ready' | 'processing' | 'queued' | 'error';

/** Složka klienta v Přepisech. */
export interface TranscriptClientDTO {
  slug: string;
  name: string;
  meetings: number;
  lastMeetingAt: string | null;
  /** Nahrávky, které ještě nemají hotový přepis (null = Sam to nehlásí). */
  pending: number | null;
}

export interface TranscriptMeetingDTO {
  meetingId: string;
  /** `YYYY-MM-DDTHH:MM` */
  start: string;
  title: string;
  durationSec: number | null;
  status: TranscriptStatus | null;
  /** Doplňující text ke stavu (např. „čeká na přepis od 14:05“). */
  statusNote?: string | null;
  sharepoint?: string | null;
}

/** `[2026-10-12 10:32, 00:54:29]` (i `T` místo mezery, čas i jako `54:29`). */
export const TRANSCRIPT_REF_RE = /\[(\d{4}-\d{2}-\d{2})[ T](\d{1,2}:\d{2}),\s*(\d{1,2}:\d{2}(?::\d{2})?)\]/g;

export function parseTimecode(s: string): number | null {
  const p = s.split(':').map(Number);
  if (p.some((n) => !Number.isFinite(n) || n < 0)) return null;
  if (p.length === 2) return p[0] * 60 + p[1];
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  return null;
}

export function formatTimecode(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const pad = (s: string) => s.padStart(5, '0');

/** Odkazy z textu (mimo bloky kódu je hledá volající). */
export function findTranscriptRefs(text: string): (TranscriptRef & { index: number; length: number })[] {
  const out: (TranscriptRef & { index: number; length: number })[] = [];
  for (const m of text.matchAll(TRANSCRIPT_REF_RE)) {
    const t = parseTimecode(m[3]);
    if (t === null) continue;
    out.push({ start: `${m[1]}T${pad(m[2])}`, t, index: m.index!, length: m[0].length });
  }
  return out;
}

/** Odkaz do Markdownu: fragment, který appka vykreslí jako tlačítko do panelu přepisu. */
export function transcriptHref(r: TranscriptRef): string {
  return `#prepis=${r.start}&t=${r.t}`;
}

export function parseTranscriptHref(href: string | undefined): TranscriptRef | null {
  const m = href?.match(/^#prepis=(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})&t=(\d+)$/);
  return m ? { start: m[1], t: Number(m[2]) } : null;
}

/** Nahradí odkazy v Markdownu odkazy `[00:54:29](#prepis=…)`; bloky kódu nechá být. */
export function linkTranscriptRefs(markdown: string): string {
  return markdown
    .split(/(```[\s\S]*?(?:```|$))/)
    .map((part, i) => {
      if (i % 2 === 1) return part;
      let out = '';
      let last = 0;
      for (const r of findTranscriptRefs(part)) {
        out += part.slice(last, r.index) + `[${formatTimecode(r.t)}](${transcriptHref(r)})`;
        last = r.index + r.length;
      }
      return out + part.slice(last);
    })
    .join('');
}

/** Segment, ke kterému odkaz vede: poslední, který začíná nejpozději v daném čase. */
export function segmentIndexAt(segments: TranscriptSegment[], t: number): number {
  let best = 0;
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].t <= t) best = i;
    else break;
  }
  return best;
}

/**
 * Odkaz na nahrávku v SharePointu tak, aby se otevřela v přehrávači (Stream) v prohlížeči od daného času,
 * místo stažení souboru. Nahrávky zůstávají jen v SharePointu; přehrává je SharePoint s přihlášením uživatele.
 * `web=1` = otevřít v prohlížeči, `nav` = Stream: začátek přehrávání v sekundách. Jiné hostitele než SharePoint nemění.
 */
export function sharepointPlayerUrl(url: string, startSec = 0): string {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || !/\.sharepoint\.com$/i.test(u.hostname)) return url;
    u.searchParams.delete('download');
    u.searchParams.set('web', '1');
    if (startSec > 0) u.searchParams.set('nav', JSON.stringify({ playbackOptions: { startTimeInSeconds: Math.floor(startSec) } }));
    else u.searchParams.delete('nav');
    return u.toString();
  } catch {
    return url;
  }
}

/** Shrnutí klienta od Samovy pipeline (aktualizuje ho po každé schůzce). Texty v Markdownu s citacemi přepisů. */
export interface TranscriptSummaryData {
  /** Odstavec o aktuálním stavu spolupráce. */
  status: string;
  /** Platné dohody a rozhodnutí, otevřené úkoly, termíny, vývoj v čase, otevřené otázky, schůzky (v pořadí od Sama). */
  sections: { title: string; md: string }[];
  /** Karta klienta: základní info, lidé, slovník. */
  card: { title: string; md: string }[];
  /** Začátek schůzky, po které se shrnutí naposledy aktualizovalo (`YYYY-MM-DD HH:MM`). */
  updatedAfterMeeting: string | null;
  updatedAt: string | null;
  meetings: number | null;
}

/** Shrnutí klienta pro záložku Přepisy. */
export interface ClientSummaryDTO extends TranscriptSummaryData {
  /** Začátky schůzek klienta (`YYYY-MM-DDTHH:MM`): klikací jsou jen citace, které míří na schůzku tohoto klienta. */
  verified: string[];
}
